import type { DietLog } from "@couples/shared";

import { supabase } from "../supabase";

interface LogRow {
  id: string;
  couple_id: string;
  author_id: string;
  logged_on: string;
  weight: number | string;
  body_fat: number | string | null;
  meal: string | null;
  // meal_* 列は分割後(→ 20260830111531)に追加されたもの。マイグレーション適用前の
  // 環境では返ってこないので、任意にしておく
  meal_breakfast?: string | null;
  meal_lunch?: string | null;
  meal_dinner?: string | null;
  meal_snack?: string | null;
  // calorie_* 列も meal_* と同じく後から追加されたもの(→ 20260830152154)。
  // マイグレーション適用前の環境では返ってこないので、任意にしておく
  calorie_breakfast?: number | string | null;
  calorie_lunch?: number | string | null;
  calorie_dinner?: number | string | null;
  calorie_snack?: number | string | null;
  exercise: string | null;
  note: string | null;
  created_at: string;
}

/** numeric 列は PostgREST の設定次第で文字列で返る。数として扱う前に必ずここを通す */
function numberOf(value: number | string): number {
  return typeof value === "number" ? value : Number(value);
}

function numberOrNullOf(value: number | string | null | undefined): number | null {
  return value === null || value === undefined ? null : numberOf(value);
}

function toLog(row: LogRow): DietLog {
  return {
    id: row.id,
    coupleId: row.couple_id,
    authorId: row.author_id,
    loggedOn: row.logged_on,
    weight: numberOf(row.weight),
    bodyFat: row.body_fat === null ? null : numberOf(row.body_fat),
    meal: row.meal,
    mealBreakfast: row.meal_breakfast ?? null,
    mealLunch: row.meal_lunch ?? null,
    mealDinner: row.meal_dinner ?? null,
    mealSnack: row.meal_snack ?? null,
    calorieBreakfast: numberOrNullOf(row.calorie_breakfast),
    calorieLunch: numberOrNullOf(row.calorie_lunch),
    calorieDinner: numberOrNullOf(row.calorie_dinner),
    calorieSnack: numberOrNullOf(row.calorie_snack),
    exercise: row.exercise,
    note: row.note,
    createdAt: row.created_at,
  };
}

/**
 * 二人ぶんの記録を新しい順に引く。
 * グラフも一覧も直近しか使わないので、件数で頭打ちにして全期間は取りに行かない。
 *
 * meal_* 列がまだ無い環境(マイグレーション適用前)でも一覧が壊れないよう、
 * 列を名指しせずに取る(→ lib/media/tags.ts と同じ形)。
 */
export async function fetchLogs(limit = 200): Promise<DietLog[]> {
  const { data, error } = await supabase
    .from("diet_logs")
    .select("*")
    .order("logged_on", { ascending: false })
    .limit(limit);
  if (error) throw error;
  const rows = data as LogRow[];
  return rows.map(toLog);
}

export interface DietLogInput {
  /** `YYYY-MM-DD` */
  loggedOn: string;
  weight: number;
  bodyFat: number | null;
  mealBreakfast: string | null;
  mealLunch: string | null;
  mealDinner: string | null;
  mealSnack: string | null;
  calorieBreakfast: number | null;
  calorieLunch: number | null;
  calorieDinner: number | null;
  calorieSnack: number | null;
  exercise: string | null;
  note: string | null;
}

/**
 * その日ぶんを書く。1人1日1件なので、同じ日に二度書いたら上書きになる
 * (unique (author_id, logged_on) に対する upsert)。
 * couple_id / author_id は DB 側の既定値でセッションから埋まる。
 */
export async function saveLog(input: DietLogInput): Promise<void> {
  const { error } = await supabase.from("diet_logs").upsert(
    {
      logged_on: input.loggedOn,
      weight: input.weight,
      body_fat: input.bodyFat,
      meal_breakfast: input.mealBreakfast,
      meal_lunch: input.mealLunch,
      meal_dinner: input.mealDinner,
      meal_snack: input.mealSnack,
      calorie_breakfast: input.calorieBreakfast,
      calorie_lunch: input.calorieLunch,
      calorie_dinner: input.calorieDinner,
      calorie_snack: input.calorieSnack,
      exercise: input.exercise,
      note: input.note,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "author_id,logged_on" },
  );
  if (error) throw error;
}

/** 消せるのは自分の記録だけ(RLS)。相手の行を指定しても0件更新で終わる */
export async function deleteLog(id: string): Promise<void> {
  const { error } = await supabase.from("diet_logs").delete().eq("id", id);
  if (error) throw error;
}
